import React, { useState, useEffect, useCallback } from 'react';
import Card from '@/components/UI/Card';
import FormSelect from '@/components/UI/FormSelect';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { TradingAccount } from '@einhaus/project314-types';

interface TradingAccountCardProps {
    portfolioId: string | undefined;
    tradingAccountId: number | null | undefined;
    tradingAccounts: TradingAccount[];
}

const TradingAccountCard: React.FC<TradingAccountCardProps> = ({ portfolioId, tradingAccountId, tradingAccounts }) => {
    const queryClient = useQueryClient();
    const [selectedTradingAccount, setSelectedTradingAccount] = useState<string>('');

    // Update selected trading account when portfolio data is loaded
    useEffect(() => {
        if (tradingAccountId) {
            setSelectedTradingAccount(tradingAccountId.toString());
        }
    }, [tradingAccountId]);

    // Mutation for updating trading account
    const tradingAccountMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });
            }
        }),
        {
            successMessage: 'Trading account updated successfully',
            fallbackErrorMessage: 'Failed to update trading account'
        }
    );

    const handleTradingAccountChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const accountId = e.target.value;
            setSelectedTradingAccount(accountId);

            if (!portfolioId || !accountId) return;

            void tradingAccountMutation.mutateAsync({
                portfolioId: parseInt(portfolioId),
                settingType: 'tradingAccount',
                value: {
                    id: parseInt(accountId)
                }
            });
        },
        [portfolioId, tradingAccountMutation]
    );

    return (
        <Card className="mb-4" title="Trading Account">
            <div className="flex items-center">
                <div className="grow mr-2 w-full">
                    <FormSelect
                        id="tradingAccountSelect"
                        options={[
                            { value: '', label: 'Choose a Trading Account' },
                            ...tradingAccounts.map((account) => ({ value: account.id.toString(), label: account.name }))
                        ]}
                        value={selectedTradingAccount}
                        onChange={handleTradingAccountChange}
                    />
                </div>
            </div>
        </Card>
    );
};

export default TradingAccountCard;
